import { Injectable, inject } from '@angular/core';
import { openDB, type IDBPDatabase } from 'idb';

import type { CheckInPayload, CheckOutStoredPayload, DeviceInfo, FingerprintId } from '@core/remito.model';
import { LogService } from './log.service';

/**
 * Remito pendiente de sincronización con metadata
 */

export interface PendingRemitoImage {
  imageKey: string;
  image: string;
  pageNumber: number;
}

interface PendingRemitoImages {
  imageKeys: string[];
  images: string[];
}

// Nuevo PendingRemito alineado al nuevo modelo
export interface PendingRemito {
  id: string;
  qrCode: string;
  imageKeys: string[];
  images: string[];
  createdAt: number;
  sessionId: string;
  checkIn: CheckInPayload;
  checkout?: CheckOutStoredPayload;
  observation: string;
  syncAttempts: number;
  status: 'pending' | 'syncing' | 'failed' | 'synced';
  error?: string;
  lastAttemptAt?: number;
  syncStatus?: 'pending' | 'failed' | 'synced';
  syncError?: string;
  lastSyncAttemptAt?: number;
}

/**
 * Servicio de almacenamiento local usando IndexedDB.
 *
 * Maneja la persistencia de remitos pendientes de sincronización
 * cuando no hay conexión a internet.
 *
 * Base de datos: 'remitos-pwa'
 * Object Store: 'pending-remitos'
 */
@Injectable({ providedIn: 'root' })
export class IndexedDbService {
  private readonly DB_NAME = 'remitos-pwa';
  private readonly DB_VERSION = 5;
  private readonly STORE_NAME = 'pending-remitos';
  private readonly STATUS_INDEX = 'status';
  private readonly REMITO_CODE_INDEX = 'remitoCode';
  private readonly QR_CODE_INDEX = 'qrCode';
  private readonly DEFAULT_DEVICE_INFO: DeviceInfo = {
    deviceType: 'mobile',
    os: 'Unknown',
    version: 'unknown',
    mobile: true,
    resolution: '0x0',
    networkType: 'offline',
    timeHour: 0,
    timezone: 'unknown'
  };

  private db: IDBPDatabase | null = null;
  private readonly logger = inject(LogService);

  /**
   * Inicializa la base de datos IndexedDB
   */
  async init(): Promise<void> {
    if (this.db) return;

    try {
      this.db = await openDB(this.DB_NAME, this.DB_VERSION, {
        upgrade: (db, oldVersion, _newVersion, transaction) => {
          if (!db.objectStoreNames.contains(this.STORE_NAME)) {
            const newStore = db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
            newStore.createIndex(this.STATUS_INDEX, 'status');
            newStore.createIndex('createdAt', 'createdAt');
            newStore.createIndex('syncAttempts', 'syncAttempts');
            newStore.createIndex(this.REMITO_CODE_INDEX, 'remito.DeliveryNote.QrCode', { unique: false });
            newStore.createIndex(this.QR_CODE_INDEX, 'qrCode', { unique: false });
            return;
          }

          const existingStore = transaction.objectStore(this.STORE_NAME);
          if (!existingStore.indexNames.contains(this.REMITO_CODE_INDEX)) {
            existingStore.createIndex(this.REMITO_CODE_INDEX, 'remito.DeliveryNote.QrCode', { unique: false });
          }

          if (!existingStore.indexNames.contains(this.QR_CODE_INDEX)) {
            existingStore.createIndex(this.QR_CODE_INDEX, 'qrCode', { unique: false });
          }

          if (!existingStore.indexNames.contains(this.STATUS_INDEX)) {
            existingStore.createIndex(this.STATUS_INDEX, 'status');
          }

          if (!existingStore.indexNames.contains('createdAt')) {
            existingStore.createIndex('createdAt', 'createdAt');
          }

          if (!existingStore.indexNames.contains('syncAttempts')) {
            existingStore.createIndex('syncAttempts', 'syncAttempts');
          }
        },
      });
      this.logger.info('Base de datos IndexedDB inicializada');
      await this.migrateLegacyEntries();
    } catch (error) {
      this.logger.error('Error al inicializar IndexedDB', { error: error instanceof Error ? error.message : String(error) });
      throw new Error('No se pudo inicializar IndexedDB');
    }
  }

  /**
   * Guarda un remito pendiente de sincronización con su metadata de Check-in (si existe)
   */
  /**
   * Guarda un remito pendiente de sincronización (nuevo esquema)
   * @param data - Objeto con todos los campos requeridos
   */
  async savePending(data: Omit<PendingRemito, 'id' | 'syncAttempts' | 'status' | 'error' | 'lastAttemptAt'> & { id?: string }): Promise<PendingRemito> {
    await this.ensureDb();
    const imagePairs = this.normalizeImagePairs(data.images, data.imageKeys);
    const pending: PendingRemito = {
      id: data.id || this.generateId(),
      qrCode: data.qrCode,
      imageKeys: imagePairs.imageKeys,
      images: imagePairs.images,
      createdAt: Date.now(),
      sessionId: data.sessionId,
      checkIn: data.checkIn,
      checkout: data.checkout,
      observation: data.observation,
      syncAttempts: 0,
      status: 'pending',
      syncStatus: 'pending',
      // error y lastAttemptAt quedan undefined
    };

    this.logger.info('Guardando remito con check-in completo en IndexedDB', {
      id: pending.id,
      qrCode: pending.qrCode,
      sessionId: pending.sessionId,
      imageCount: pending.images.length,
      imageKeys: pending.imageKeys,
      deviceInfo: pending.checkIn.deviceInfo,
      location: pending.checkIn.location
    });

    await this.db!.add(this.STORE_NAME, pending);
    this.logger.info('Remito guardado en IndexedDB', { id: pending.id });
    return pending;
  }

  /**
   * Obtiene todos los remitos pendientes de sincronización
   */
  async getPending(includeFailed = false): Promise<PendingRemito[]> {
    await this.ensureDb();

    const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
    const index = tx.store.index(this.STATUS_INDEX);
    const pending = await index.getAll('pending');

    const convertedPending = pending.map(record => this.convertToV3(record));

    if (!includeFailed) {
      return convertedPending;
    }

    const failed = await index.getAll('failed');
    const convertedFailed = failed.map(record => this.convertToV3(record));
    return [...convertedPending, ...convertedFailed];
  }

  /**
   * Obtiene todos los remitos almacenados (sin filtrar por estado)
   */
  async getAll(): Promise<PendingRemito[]> {
    await this.ensureDb();

    const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
    const records = await tx.store.getAll();
    return records.map(record => this.convertToV3(record));
  }

  /**
   * Obtiene remitos por sessionId (opcionalmente incluye fallidos)
   */
  async getBySessionId(sessionId: string, includeFailed = true): Promise<PendingRemito[]> {
    if (!sessionId?.trim()) {
      return [];
    }
    const all = await this.getAll();
    const filtered = all.filter(item => item.sessionId === sessionId && item.status !== 'synced');
    if (includeFailed) {
      return filtered;
    }
    return filtered.filter(item => item.status !== 'failed');
  }

  /**
   * Verifica si existe un remito pendiente (o fallido) con el mismo código QR
   */
  async existsByQrCode(qrCode: string): Promise<boolean> {
    if (!qrCode?.trim()) {
      return false;
    }

    await this.ensureDb();
    const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
    const index = tx.store.index(this.QR_CODE_INDEX);
    const record = await index.get(qrCode.trim());

    if (!record) {
      return false;
    }

    return record.status === 'pending' || record.status === 'syncing' || record.status === 'failed';
  }

  /**
   * Obtiene un remito pendiente por su código QR
   */
  async getByQrCode(qrCode: string): Promise<PendingRemito | null> {
    if (!qrCode?.trim()) {
      return null;
    }

    await this.ensureDb();
    const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
    const index = tx.store.index(this.QR_CODE_INDEX);
    const record = await index.get(qrCode.trim());

    if (!record) {
      return null;
    }

    return this.convertToV3(record);
  }

  /**
   * Obtiene un remito por ID
   */
  async getById(id: string): Promise<PendingRemito | undefined> {
    await this.ensureDb();
    const record = await this.db!.get(this.STORE_NAME, id);
    if (!record) {
      return undefined;
    }
    return this.convertToV3(record);
  }

  /**
   * Actualiza el estado de un remito
   */
  async updateStatus(
    id: string,
    status: PendingRemito['status'],
    error?: string
  ): Promise<void> {
    await this.ensureDb();

    const pending = await this.getById(id);
    if (!pending) {
      throw new Error(`Remito ${id} no encontrado`);
    }

    const now = Date.now();
    pending.status = status;
    pending.lastAttemptAt = now;
    pending.lastSyncAttemptAt = now;
    pending.syncStatus = status === 'synced' ? 'synced' : (status === 'failed' ? 'failed' : 'pending');
    if (error) {
      pending.error = error;
      pending.syncError = error;
    }

    await this.db!.put(this.STORE_NAME, pending);
    this.logger.info('Estado de remito actualizado en IndexedDB', { id, status });
  }

  /**
   * Agrega el nodo checkout a todos los remitos existentes (si no lo tienen)
   * y devuelve los remitos actualizados.
   */
  async setCheckOutForAll(checkout: PendingRemito['checkout']): Promise<PendingRemito[]> {
    await this.ensureDb();
    const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
    const records = await tx.store.getAll();
    const updated: PendingRemito[] = [];

    for (const record of records) {
      const converted = this.convertToV3(record);
      const next: PendingRemito = {
        ...converted,
        checkout: converted.checkout ?? checkout
      };
      await tx.store.put(next);
      updated.push(next);
    }

    await tx.done;
    return updated;
  }

  async update(remito: PendingRemito): Promise<void> {
    await this.ensureDb();
    await this.db!.put(this.STORE_NAME, remito);
  }

  /**
   * Incrementa el contador de intentos de sincronización
   */
  async incrementAttempts(id: string): Promise<void> {
    await this.ensureDb();

    const pending = await this.getById(id);
    if (!pending) {
      throw new Error(`Remito ${id} no encontrado`);
    }

    pending.syncAttempts += 1;
    const now = Date.now();
    pending.lastAttemptAt = now;
    pending.lastSyncAttemptAt = now;

    await this.db!.put(this.STORE_NAME, pending);
  }

  /**
   * Elimina un remito después de sincronizarse exitosamente
   */
  async delete(id: string): Promise<void> {
    await this.ensureDb();
    await this.db!.delete(this.STORE_NAME, id);
    this.logger.info('Remito eliminado de IndexedDB', { id });
  }

  /**
   * Obtiene el conteo de remitos pendientes
   */
  async getCount(includeFailed = true): Promise<number> {
    const counts = await this.getStatusCounts();
    const unsynced = counts.pending + counts.syncing + (includeFailed ? counts.failed : 0);
    return unsynced;
  }

  async getStatusCounts(): Promise<Record<PendingRemito['status'], number>> {
    await this.ensureDb();

    const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
    const records = await tx.store.getAll();
    const counts = {
      pending: 0,
      syncing: 0,
      failed: 0,
      synced: 0
    } as Record<PendingRemito['status'], number>;

    for (const record of records) {
      const status = (record?.status ?? 'pending') as PendingRemito['status'];
      if (status in counts) {
        counts[status] += 1;
      } else {
        counts.pending += 1;
      }
    }

    return counts;
  }

  /**
   * Limpia todos los remitos sincronizados exitosamente
   */
  async cleanSynced(): Promise<void> {
    await this.ensureDb();

    const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
    const index = tx.store.index(this.STATUS_INDEX);
    const synced = await index.getAllKeys('synced');

    for (const key of synced) {
      await tx.store.delete(key);
    }

    await tx.done;
    this.logger.info('Remitos sincronizados eliminados de IndexedDB', { count: synced.length });
  }

  /**
   * Migración de registros legacy y v2→v3
   * Convierte registros antiguos al nuevo formato PendingRemito
   */
  private async migrateLegacyEntries(): Promise<void> {
    if (!this.db) return;
    const tx = this.db.transaction(this.STORE_NAME, 'readwrite');
    const records = await tx.store.getAll();
    let migrated = 0;
    for (const record of records) {
      if (this.hasKey(record, 'qrCode')) {
        continue;
      }
      const recordId = this.readString(record, ['id']);
      try {
        // Adaptar v2 a v3
        const converted = this.convertToV3(record);
        await tx.store.put(converted);
        migrated += 1;
      } catch (error) {
        this.logger.warn('No se pudo migrar remito v2', {
          id: recordId ?? 'unknown',
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
    await tx.done;
    if (migrated > 0) {
      this.logger.info('Migración de remitos pendientes completada', { migrated });
    }
  }

  /**
   * Convierte un registro legacy o v2 a PendingRemito v3
   */
  private convertToV3(record: unknown): PendingRemito {
    if (!this.isObjectRecord(record)) {
      throw new Error('No se pudo migrar registro: formato desconocido');
    }

    if (this.isV3Record(record)) {
      return this.normalizeV3Record(record);
    }

    if (this.isLegacyRecord(record)) {
      return this.mapLegacyRecord(record);
    }

    throw new Error('No se pudo migrar registro: formato desconocido');
  }

  private normalizeV3Record(record: Record<string, unknown>): PendingRemito {
    const status = this.normalizeStatus(this.readAtPath(record, ['status']));
    const sessionId = this.readString(record, ['sessionId']) ?? this.readString(record, ['checkIn', 'id']) ?? 'unknown';
    const checkIn = this.normalizeCheckIn(this.readAtPath(record, ['checkIn']), sessionId);
    const checkout = this.normalizeCheckout(
      this.readAtPath(record, ['checkout']),
      this.readAtPath(record, ['checkOut'])
    );

    const imagePairs = this.normalizeImagePairs(
      this.readAtPath(record, ['images']),
      this.readAtPath(record, ['imageKeys']),
      this.readString(record, ['imageKey']),
      this.firstString(record, [['image'], ['photo']])
    );

    return {
      id: this.readString(record, ['id']) ?? this.generateId(),
      qrCode: this.readString(record, ['qrCode']) ?? 'unknown',
      imageKeys: imagePairs.imageKeys,
      images: imagePairs.images,
      createdAt: this.readNumber(record, ['createdAt']) ?? Date.now(),
      sessionId,
      checkIn,
      checkout,
      observation: this.readString(record, ['observation']) ?? '',
      syncAttempts: this.readNumber(record, ['syncAttempts']) ?? 0,
      status,
      error: this.readString(record, ['error']),
      lastAttemptAt: this.readNumber(record, ['lastAttemptAt']),
      syncStatus: this.normalizeSyncStatus(this.readAtPath(record, ['syncStatus']), status),
      syncError: this.readString(record, ['syncError']) ?? this.readString(record, ['error']),
      lastSyncAttemptAt: this.readNumber(record, ['lastSyncAttemptAt']) ?? this.readNumber(record, ['lastAttemptAt'])
    };
  }

  private mapLegacyRecord(record: Record<string, unknown>): PendingRemito {
    const fallbackSessionId = this.readString(record, ['remito', 'CheckIn', 'Id']) ?? 'unknown';
    const checkIn = this.normalizeCheckIn(this.readAtPath(record, ['remito', 'CheckIn']), fallbackSessionId);
    const status = this.normalizeStatus(this.readAtPath(record, ['status']));
    const checkout = this.normalizeCheckout(
      this.readAtPath(record, ['checkout']),
      this.readAtPath(record, ['checkOut'])
    );

    const imagePairs = this.normalizeImagePairs(
      this.readAtPath(record, ['images']),
      this.readAtPath(record, ['imageKeys']),
      this.readString(record, ['remito', 'DeliveryNote', 'Image']),
      this.firstString(record, [['photo'], ['image']])
    );

    return {
      id: this.readString(record, ['id']) ?? this.generateId(),
      qrCode: this.readString(record, ['remito', 'DeliveryNote', 'QrCode']) ?? 'unknown',
      imageKeys: imagePairs.imageKeys,
      images: imagePairs.images,
      createdAt: this.readNumber(record, ['createdAt']) ?? Date.now(),
      sessionId: fallbackSessionId,
      checkIn,
      checkout,
      observation: this.readString(record, ['remito', 'DeliveryNote', 'Observation']) ?? '',
      syncAttempts: this.readNumber(record, ['syncAttempts']) ?? 0,
      status,
      syncStatus: this.normalizeSyncStatus(this.readAtPath(record, ['syncStatus']), status),
      syncError: this.readString(record, ['syncError']) ?? this.readString(record, ['error']),
      error: this.readString(record, ['error']),
      lastAttemptAt: this.readNumber(record, ['lastAttemptAt']),
      lastSyncAttemptAt: this.readNumber(record, ['lastSyncAttemptAt']) ?? this.readNumber(record, ['lastAttemptAt'])
    };
  }

  private normalizeCheckIn(rawValue: unknown, fallbackId: string): CheckInPayload {
    const checkInRecord = this.isObjectRecord(rawValue) ? rawValue : {};
    const fingerprintId = this.readString(checkInRecord, ['fingerprintId'])
      ?? this.readString(checkInRecord, ['FingerprintId'])
      ?? this.readString(checkInRecord, ['DeviceInfo', 'fingerprintId']);

    return {
      id: this.readString(checkInRecord, ['id']) ?? this.readString(checkInRecord, ['Id']) ?? fallbackId,
      createdAt: this.normalizeDate(
        this.readAtPath(checkInRecord, ['createdAt']) ?? this.readAtPath(checkInRecord, ['CreatedAt'])
      ),
      location: {
        latitude: this.readNumber(checkInRecord, ['location', 'latitude'])
          ?? this.readNumber(checkInRecord, ['Location', 'Latitude'])
          ?? 0,
        longitude: this.readNumber(checkInRecord, ['location', 'longitude'])
          ?? this.readNumber(checkInRecord, ['Location', 'Longitude'])
          ?? 0,
        precision: this.readNumber(checkInRecord, ['location', 'precision'])
          ?? this.readNumber(checkInRecord, ['Location', 'Precision'])
          ?? 0
      },
      deviceInfo: this.normalizeDeviceInfo(
        this.readAtPath(checkInRecord, ['deviceInfo']) ?? this.readAtPath(checkInRecord, ['DeviceInfo']),
        fingerprintId
      )
    };
  }

  private normalizeCheckout(primary: unknown, fallback: unknown): PendingRemito['checkout'] | undefined {
    const selected = this.isObjectRecord(primary) ? primary : (this.isObjectRecord(fallback) ? fallback : undefined);
    if (!selected) {
      return undefined;
    }

    const checkout = selected as CheckOutStoredPayload & { fingerprintId?: FingerprintId };
    const hasCheckoutShape = this.readString(selected, ['createdAt']) && this.readAtPath(selected, ['location']);
    if (!hasCheckoutShape) {
      return checkout;
    }

    return {
      ...checkout,
      deviceInfo: this.normalizeDeviceInfo(
        this.readAtPath(selected, ['deviceInfo']),
        this.readString(selected, ['fingerprintId'])
      )
    };
  }

  private normalizeDeviceInfo(rawValue: unknown, fallbackFingerprintId?: FingerprintId): DeviceInfo {
    const deviceInfo = this.isObjectRecord(rawValue) ? rawValue : {};
    const normalized: DeviceInfo = {
      ...this.DEFAULT_DEVICE_INFO,
      deviceType: this.normalizeDeviceType(this.readAtPath(deviceInfo, ['deviceType'])),
      os: this.readString(deviceInfo, ['os']) ?? this.DEFAULT_DEVICE_INFO.os,
      version: this.readString(deviceInfo, ['version']) ?? this.DEFAULT_DEVICE_INFO.version,
      mobile: this.readBoolean(deviceInfo, ['mobile']) ?? this.DEFAULT_DEVICE_INFO.mobile,
      resolution: this.readString(deviceInfo, ['resolution']) ?? this.DEFAULT_DEVICE_INFO.resolution,
      networkType: this.readString(deviceInfo, ['networkType']) ?? this.DEFAULT_DEVICE_INFO.networkType,
      timeHour: this.readNumber(deviceInfo, ['timeHour']) ?? this.DEFAULT_DEVICE_INFO.timeHour,
      timezone: this.readString(deviceInfo, ['timezone']) ?? this.DEFAULT_DEVICE_INFO.timezone
    };

    const fingerprintId = this.readString(deviceInfo, ['fingerprintId']) ?? fallbackFingerprintId;
    if (fingerprintId) {
      normalized.fingerprintId = fingerprintId;
    }

    return normalized;
  }

  private normalizeDeviceType(value: unknown): DeviceInfo['deviceType'] {
    if (value === 'mobile' || value === 'tablet' || value === 'desktop') {
      return value;
    }
    return this.DEFAULT_DEVICE_INFO.deviceType;
  }

  private normalizeStatus(value: unknown): PendingRemito['status'] {
    if (value === 'pending' || value === 'syncing' || value === 'failed' || value === 'synced') {
      return value;
    }
    return 'pending';
  }

  private normalizeSyncStatus(value: unknown, status: PendingRemito['status']): PendingRemito['syncStatus'] {
    if (value === 'pending' || value === 'failed' || value === 'synced') {
      return value;
    }
    return status === 'syncing' ? 'pending' : (status === 'failed' ? 'failed' : (status === 'synced' ? 'synced' : 'pending'));
  }

  private normalizeDate(value: unknown): string {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return new Date(value).toISOString();
    }
    return new Date(Date.now()).toISOString();
  }

  private isV3Record(record: Record<string, unknown>): boolean {
    return this.hasKey(record, 'qrCode')
      && (this.hasKey(record, 'images') || this.hasKey(record, 'imageKeys') || this.hasKey(record, 'imageKey'))
      && this.hasKey(record, 'sessionId')
      && !this.hasKey(record, 'photo');
  }

  private normalizeImagePairs(
    rawImages: unknown,
    rawImageKeys?: unknown,
    legacyImageKey?: string,
    legacyImage?: string
  ): PendingRemitoImages {
    const fromObjectImages = this.normalizeObjectImagePairs(rawImages);
    if (fromObjectImages.images.length > 0) {
      return fromObjectImages;
    }

    const fromParallelArrays = this.normalizeParallelImagePairs(rawImages, rawImageKeys);
    if (fromParallelArrays.images.length > 0) {
      return fromParallelArrays;
    }

    if (legacyImageKey && legacyImage) {
      return {
        imageKeys: [legacyImageKey],
        images: [legacyImage]
      };
    }

    return { imageKeys: [], images: [] };
  }

  private normalizeObjectImagePairs(rawImages: unknown): PendingRemitoImages {
    if (!Array.isArray(rawImages) || rawImages.some(item => !this.isObjectRecord(item))) {
      return { imageKeys: [], images: [] };
    }

    const pairs = rawImages
      .filter((item): item is Record<string, unknown> => this.isObjectRecord(item))
      .map((item, index) => ({
        imageKey: this.readString(item, ['imageKey']) ?? `image-${index + 1}.jpg`,
        image: this.readString(item, ['image']) ?? '',
        pageNumber: this.readNumber(item, ['pageNumber']) ?? index + 1
      }))
      .filter(item => !!item.imageKey && !!item.image)
      .sort((a, b) => a.pageNumber - b.pageNumber);

    return {
      imageKeys: pairs.map(item => item.imageKey),
      images: pairs.map(item => item.image)
    };
  }

  private normalizeParallelImagePairs(rawImages: unknown, rawImageKeys: unknown): PendingRemitoImages {
    if (!Array.isArray(rawImages) || !Array.isArray(rawImageKeys)) {
      return { imageKeys: [], images: [] };
    }

    const length = Math.min(rawImages.length, rawImageKeys.length);
    const pairs = Array.from({ length }, (_, index) => ({
      image: rawImages[index],
      imageKey: rawImageKeys[index]
    })).filter((item): item is { image: string; imageKey: string } =>
      typeof item.image === 'string'
      && item.image.length > 0
      && typeof item.imageKey === 'string'
      && item.imageKey.length > 0
    );

    return {
      imageKeys: pairs.map(item => item.imageKey),
      images: pairs.map(item => item.image)
    };
  }

  private isLegacyRecord(record: Record<string, unknown>): boolean {
    return this.hasKey(record, 'remito')
      || (this.hasKey(record, 'photo') && !this.hasKey(record, 'image'));
  }

  private hasKey(source: unknown, key: string): source is Record<string, unknown> {
    return this.isObjectRecord(source) && key in source;
  }

  private isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private readAtPath(source: unknown, path: readonly string[]): unknown {
    let current: unknown = source;
    for (const part of path) {
      if (!this.isObjectRecord(current) || !(part in current)) {
        return undefined;
      }
      current = current[part];
    }
    return current;
  }

  private readString(source: unknown, path: readonly string[]): string | undefined {
    const value = this.readAtPath(source, path);
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  }

  private readNumber(source: unknown, path: readonly string[]): number | undefined {
    const value = this.readAtPath(source, path);
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
  }

  private readBoolean(source: unknown, path: readonly string[]): boolean | undefined {
    const value = this.readAtPath(source, path);
    return typeof value === 'boolean' ? value : undefined;
  }

  private firstString(source: unknown, paths: readonly (readonly string[])[]): string | undefined {
    for (const path of paths) {
      const value = this.readString(source, path);
      if (value) {
        return value;
      }
    }
    return undefined;
  }

  /**
   * Asegura que la base de datos esté inicializada
   */
  private async ensureDb(): Promise<void> {
    if (!this.db) {
      await this.init();
    }
  }

  /**
   * Genera un ID único para el remito local
   */
  private generateId(): string {
    return `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
